import { createTheme, darken, lighten } from '@material-ui/core/styles';
import { IconChevronDown } from '@tabler/icons-react';
// See all theme options here: https://material-ui.com/customization/default-theme/
export const colors = {
  primary: '#523AE7',
  primaryFaded: 'rgba(82, 58, 231, 0.1)',
  primaryLight: '#8673FF',
  secondary: '#F1436D',
  secondaryFaded: 'rgba(208, 16, 62, 0.1)',
  error: '#F1436D',
  errorFaded: 'rgba(208, 16, 62, 0.1)',
  success: '#298000',
  successFaded: 'rgba(41, 128, 0, 0.1)',
  darkGreen: '#3CBB01',
  red: '#fd5855',
  lightRed: '#E04000',
  darkRed: '#a01000',
  white: '#FFFFFF',
  white25opacity: '#FFFFFF40',
  white50opacity: '#FFFFFF80',
  whiteFaded: '#FFFFFF4D',
  black: '#0A0821',
  lightGrey: '#F8F8F8', // deprecated
  mediumLightGrey: '#F2F2F2', // deprecated
  grey: '#C4C4C4', // deprecated
  mediumDarkGrey: '#8F8F8F', // deprecated
  darkGrey: '#565656', // deprecated

  pink200: '#FFCCDA',
  pink400: '#FF7096',
  pink600: '#990029',
  orange200: '#FFC199',
  orange400: '#FF8C42',
  orange600: '#993C00',
  yellow200: '#FFE599',
  yellow400: '#F7B801',
  yellow600: '#987201',
  green200: '#CEFDE1',
  green400: '#08A045',
  green600: '#05612A',
  blue200: '#CFE8FC',
  blue400: '#2196F3',
  blue600: '#053861',
  purple200: '#D3D0FB',
  purple400: '#4D41F1',
  purple600: '#140B8E',

  neutrals600: '#0A0821',
  neutrals500: '#262445',
  neutrals400: '#56556C',
  neutrals300: '#9F9EAB',
  neutrals200: '#CFCED5',
  neutrals100: '#E7E7EA',
  neutrals50: '#F7F7F8',
  greyOffcolor: '#858585',
  greyHalfOpacity: '#C4C4C47F',
  pureGray: '#828282',
  green: '#17c58F',
  lightGreen: '#95BF46',
  blue: '#509ebc',
  darkBlue: '#40465a',
  darkBlueLighter: '#4e566d',
  lightBlue: '#67cFf7',
  navy: '#00063D',
  darkPurple: '#000429',
  purpleFiller: '#333763',
  purpleWhiteMixed: '#EEEBFD',
  logoPurple: '#848DD3',
  logoRed: '#DD2828',
  logoYellow: '#F9E99B',
  warning: '#936A00',
  warningYellow: 'rgba(229, 178, 25, 0.2)',
  pink: '#FFA8EC',
  yellow: '#FFD965',
} as const;

type ColorKeys = keyof typeof colors;
type ColorValues = typeof colors[ColorKeys];

export type Colors = Record<ColorKeys, ColorValues>;

const theme = createTheme({
  palette: {
    colors,
    primary: {
      main: colors.primary,
      // @ts-expect-error extend mui theme
      faded: colors.primaryFaded,
    },
    secondary: {
      main: colors.secondary,
      // @ts-expect-error extend mui theme
      faded: colors.secondaryFaded,
    },
    error: {
      main: colors.error,
      // @ts-expect-error extend mui theme
      faded: colors.errorFaded,
    },
    success: {
      main: colors.success,
    },
    disabled: {
      main: colors.grey,
    },
    grey: {
      // @ts-expect-error extend mui theme
      dark: colors.mediumDarkGrey,
      light: colors.lightGrey,
      main: colors.grey,
      pure: colors.pureGray,
    },
    white: {
      main: colors.white,
    },
    text: {
      primary: colors.black,
      secondary: colors.darkGrey,
      disabled: colors.mediumDarkGrey,
    },
    action: {
    },
    background: {
      // @ts-expect-error extend mui theme
      contentBreaker: colors.mediumLightGrey,
      primary: colors.lightGrey,
      dark: colors.black,
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  typography: {
    // @ts-expect-error extend mui theme
    useNextVariants: true,
    fontFamily: [
      'Inter',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '48px',
      lineHeight: '58px',
      textAlign: 'left',
      '@media (min-width:780px)': {
        fontSize: '40px',
        lineHeight: '50px',
        textAlign: 'center',
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: '32px',
      lineHeight: '42px',
      textAlign: 'left',
      '@media (min-width:780px)': {
        fontSize: '26px',
        lineHeight: '32px',
        textAlign: 'center',
      },
    },
    h6: {
      fontSize: 18,
    },
  },
  props: {
    MuiButton: {
      variant: 'contained',
      color: 'primary',
      disableElevation: true,
    },
    MuiIconButton: {
      color: 'primary',
    },
    MuiTablePagination: {
      color: 'primary',
    },
    // elevation:0 in order to resolve console warnings
    MuiPaper: {
      elevation: 0,
    },
    MuiPopover: {
      elevation: 0,
    },
    MuiDrawer: {
      elevation: 0,
    },
    MuiDialog: {
      PaperProps: {
        elevation: 0,
      },
    },
    MuiSelect: {
      IconComponent: IconChevronDown,
      MenuProps: {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: -6,
          horizontal: 'left',
        },
        getContentAnchorEl: null,
      },
    },
  },
  // @ts-expect-error extend mui theme
  shadows: ['none'],
  // TODO: fix me: below properties were move from styled components
  // and do not match material ui theme style
  font: {
    family: {
      default: '"Inter", sans-serif',
      secondary: '',
    },
    size: {
      sm: '0.85rem',
      md: '1rem',
      lg: '1.25rem',
      xl: '1.5rem',
      xxl: '2rem',
    },
  },
  border: {
    radius: {
      default: '4px',
      none: '0px',
      sm: '3px',
      md: '6px',
      lg: '8px',
      full: '9999px',
    },
    default: `1px solid ${colors.grey}`,
  },
  form: {
    input: {
      borderColor: colors.grey,
      borderWidth: '1px',
      fontSize: '0.85rem',
      padding: '0.5rem 0.5rem 0.5rem 0.8rem',
      color: colors.darkGrey,
      height: '48px',
    },
  },
  overrides: {
    MuiLink: {
      root: {
        cursor: 'pointer',
      },
    },
    MuiListItem: {
      root: {
        '&$selected': {
          color: colors.primary,
          backgroundColor: colors.primaryFaded,
        },
        '&$selected:hover': {
          backgroundColor: colors.primaryFaded,
        },
      },
      button: {
        '&:hover': {
          backgroundColor: colors.primaryFaded,
        },
      },
    },
    MuiButton: {
      root: {
        textTransform: 'none',
        padding: '5px 12px',
        fontSize: 14,
        color: colors.darkGrey,
      },
      contained: {
        minWidth: 80,
        '&$disabled': {
          color: colors.white,
          backgroundColor: colors.mediumDarkGrey,
        },
      },
      outlined: {
        minWidth: 80,
        '&$disabled': {
          border: `1px solid ${colors.mediumDarkGrey}`,
          color: colors.mediumDarkGrey,
        },
      },
      outlinedSecondary: {
        minWidth: 80,
        '&$disabled': {
          border: `1px solid ${colors.mediumDarkGrey}`,
          color: colors.mediumDarkGrey,
        },
      },
      outlinedSizeSmall: {
        fontSize: 14,
        height: 30,
      },
      containedSizeSmall: {
        fontSize: 14,
        height: 30,
      },
      outlinedSizeLarge: {
        fontSize: 14,
        fontWeight: 600,
        height: 40,
      },
      containedSizeLarge: {
        fontSize: 14,
        fontWeight: 600,
        height: 40,
      },
      textPrimary: {
        '&:hover': {
          color: lighten(colors.primary, 0.2),
          backgroundColor: 'transparent',
        },
        '&:active': {
          color: darken(colors.primary, 0.2),
        },
      },
      textSecondary: {
        '&:hover': {
          color: lighten(colors.secondary, 0.2),
          backgroundColor: 'transparent',
        },
        '&:active': {
          color: darken(colors.secondary, 0.2),
        },
      },
      textSizeLarge: {
        fontSize: 14,
        fontWeight: 600,
      },
    },
    MuiIconButton: {
      root: {
        width: 36,
        height: 36,
        padding: 0,
        borderRadius: 4,
      },
      sizeSmall: {
        width: 30,
        height: 30,
      },
    },
    MuiInputLabel: {
      root: {
        fontSize: 14,
        fontWeight: 500,
        marginBottom: 6,
        '& > svg': {
          marginLeft: 4,
          marginBottom: 2,
        },
      },
    },
    MuiFormLabel: {
      root: {
        fontSize: 14,
        fontWeight: 500,
        '& > svg': {
          marginLeft: 4,
          marginBottom: 2,
          fontSize: 16,
        },
        whiteSpace: 'nowrap',
      },
    },
    MuiFormHelperText: {
      root: {
        fontSize: 12,
        color: colors.neutrals300,
      },
    },
    MuiInputBase: {
      root: {
        minHeight: 36,
      },
      input: {
        fontSize: 14,
      },
    },
    MuiOutlinedInput: {
      input: {
        padding: '8.5px 11px',
      },
      multiline: {
        padding: '11px 11px',
      },
      notchedOutline: {
        borderColor: colors.neutrals200,
      },
      root: {
        borderRadius: 6,
        backgroundColor: colors.white,
        '&$focused': {
          '& $notchedOutline': {
            border: `1px solid ${colors.primary}`,
          },
        },
        '&:hover $notchedOutline': {
          borderColor: colors.neutrals300,
        },
      },
      inputAdornedStart: {
        paddingLeft: 8,
      },
      adornedStart: {
        '& > svg': {
          marginRight: 8,
        },
      },
    },
    MuiFormControlLabel: {
      root: {
        marginLeft: -9,
        marginRight: 0,
        color: colors.darkGrey,
      },
      label: {
        fontSize: 14,
        lineHeight: '24px',
        '& > svg': {
          marginLeft: 4,
          fontSize: 16,
        },
      },
      labelPlacementStart: {
        marginLeft: 0,
        marginRight: 0,
      },
    },
    MuiSwitch: {
      root: {
        width: 56,
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
      colorPrimary: {
        '&$checked + $track': {
          opacity: 1,
          backgroundColor: colors.primaryFaded,
          border: `1px solid ${colors.primary}`,
        },
      },
      colorSecondary: {
        '&$checked': {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
      switchBase: {
        color: colors.darkGrey,
        '&:hover': {
          backgroundColor: 'transparent',
        },
        '&$checked': {
          transform: 'translateX(16px)',
        },
      },
      thumb: {
        boxShadow: 'none',
        width: 16,
        height: 16,
        margin: '4px 0 0 3px',
      },
      track: {
        borderRadius: 50,
        height: 16,
        backgroundColor: colors.mediumLightGrey,
        border: `1px solid ${colors.darkGrey}`,
        opacity: 1,
      },
    },
    MuiCheckbox: {
      root: {
        '&&&:hover': {
          background: 'none',
        },
      },
    },
    MuiSelect: {
      root: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: 0,
        paddingBottom: 0,

        '& > :not(:last-child)': {
          marginRight: 4,
        },

        '&&:focus': {
          backgroundColor: 'transparent',
        },
      },
      icon: {
        color: colors.neutrals200,
      },
    },
    MuiMenu: {
      list: {
        borderRadius: 6,
        border: `1px solid ${colors.neutrals200}`,
        padding: 0,
      },
    },
    MuiMenuItem: {
      root: {
        fontSize: 14,
        fontWeight: 500,
      },
    },
    MuiTooltip: {
      tooltip: {
        color: colors.black,
        backgroundColor: colors.white,
        padding: '8px 12px',
        fontSize: 14,
        fontWeight: 'normal',
        lineHeight: '16px',
        border: `1px solid ${colors.mediumDarkGrey}`,
        borderRadius: 6,
      },
      arrow: {
        color: colors.white,
        '&:before': {
          border: `1px solid ${colors.mediumDarkGrey}`,
        },
      },
    },
    MuiDialog: {
      paper: {
        padding: '24px 32px',
        minWidth: 500,
        borderRadius: 8,
      },
    },
    MuiDialogTitle: {
      root: {
        padding: 0,
        marginBottom: 16,
        maxWidth: 'calc(100% - 30px)',
      },
    },
    MuiDialogContent: {
      root: {
        '&&': {
          padding: 0,
          fontSize: 14,
          overflowY: 'visible',
        },
      },
    },
    MuiDialogActions: {
      root: {
        marginTop: 16,
        padding: 0,
      },
    },
    MuiSlider: {
      root: {
        color: colors.primary,
        height: 8,
        '&$disabled > $rail': {
          backgroundImage: 'none',
        },
      },
      thumb: {
        height: 16,
        width: 16,
        backgroundColor: colors.white,
        border: `1px solid ${colors.mediumDarkGrey}`,
        marginTop: -4,
        marginLeft: -8,
        '&:focus, &:hover, &$active': {
          boxShadow: 'inherit',
        },
        '&$disabled': {
          height: 16,
          width: 16,
          marginTop: -4,
          marginLeft: -8,
        },
        '&[style="left: 0%;"]': {
          marginLeft: 0,
        },
        '&[style="left: 100%;"]': {
          marginLeft: -16,
        },
      },
      marked: {
        marginBottom: 0,
      },
      valueLabel: {
        left: 'calc(-50% - 2px)',
      },
      track: {
        backgroundColor: 'transparent',
      },
      rail: {
        height: 8,
        borderRadius: 4,
        backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.pink})`,
        opacity: 1,
      },
      mark: {
        display: 'none',
      },
      markLabel: {
        fontSize: 12,
        '&[style="left: 0%;"]': {
          transform: 'translateX(0%)',
        },
        '&[style="left: 100%;"]': {
          transform: 'translateX(-100%)',
        },
      },
      markLabelActive: {
        color: colors.darkGrey,
      },
    },
    MuiLinearProgress: {
      root: {
        height: 8,
        borderRadius: 4,
      },
      colorPrimary: {
        backgroundColor: colors.mediumLightGrey,
      },
      bar: {
        borderRadius: 4,
      },
    },
    MuiFormGroup: {
      row: {
        gap: 16,
      },
    },
  },
});

export default theme;
